import { Capacitor } from '@capacitor/core';
import {
  browserProfilingIntegration,
  replayCanvasIntegration,
  replayIntegration,
} from '@sentry/browser';
import * as Sentry from '@sentry/capacitor';
import type { CapacitorOptions } from '@sentry/capacitor';
import * as SentryVue from '@sentry/vue';
import type { App } from 'vue';
import type { Router } from 'vue-router';

/** Public DSN — same Sentry project as native Android manifest. Override via VITE_SENTRY_DSN. */
const DEFAULT_DSN =
  'https://89909794fbb1c0478bd6333ee67ae15a@sentry.f1y.ing/7';

const API_TRACE_TARGETS = [
  'localhost',
  /^https:\/\/api\.f1y\.ing\/api/,
  /^https:\/\/dev\.f1y\.ing\/api/,
];

/** Mask passwords and token-like inputs in replay; other inputs stay visible. */
const REPLAY_SENSITIVE_MASK_SELECTORS = [
  'input[type="password"]',
  'input[autocomplete*="password"]',
  'input[autocomplete="one-time-code"]',
  'input[name*="token"]',
  'input[name*="Token"]',
  'input[name*="api_key"]',
  'input[name*="api-key"]',
  'input[name*="secret"]',
  'textarea[name*="token"]',
  'textarea[name*="Token"]',
];

function parseSampleRate(value: string | undefined, fallback: number): number {
  if (value === undefined || value === '') {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : fallback;
}

function buildIntegrations(router: Router, isWeb: boolean) {
  const replayNetworkAllowUrls = [
    typeof window !== 'undefined' ? window.location.origin : '',
    /^https:\/\/api\.f1y\.ing/,
    /^https:\/\/dev\.f1y\.ing/,
  ];

  const integrations = [
    SentryVue.browserTracingIntegration({ router }),
    replayIntegration({
      maskAllText: false,
      maskAllInputs: false,
      mask: REPLAY_SENSITIVE_MASK_SELECTORS,
      blockAllMedia: false,
      networkDetailAllowUrls: replayNetworkAllowUrls,
      networkCaptureBodies: true,
    }),
  ];

  // Mapbox / Deck.gl render to canvas — without this, replays show blank maps on web.
  if (isWeb) {
    integrations.push(replayCanvasIntegration());
    integrations.push(browserProfilingIntegration());
  }

  return integrations;
}

function resolveDsn(): string {
  // CI often passes secrets.VITE_SENTRY_DSN as "" when unset; ?? would keep "" and skip init.
  const fromEnv = import.meta.env.VITE_SENTRY_DSN;
  if (typeof fromEnv === 'string' && fromEnv.trim() !== '') {
    return fromEnv.trim();
  }
  return DEFAULT_DSN;
}

export function initSentry(app: App, router: Router): void {
  const dsn = resolveDsn();

  const isWeb = !Capacitor.isNativePlatform();

  const tracesSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    1.0,
  );
  const replaysSessionSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE,
    1.0,
  );
  const replaysOnErrorSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE,
    1.0,
  );
  const profileSessionSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_PROFILE_SESSION_SAMPLE_RATE,
    1.0,
  );

  const options = {
    dsn,
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    release: `flight-track@${import.meta.env.VITE_APP_VERSION ?? __APP_VERSION__}`,
    dist: import.meta.env.VITE_SENTRY_DIST,
    sendDefaultPii: import.meta.env.VITE_SENTRY_SEND_DEFAULT_PII === 'true',
    enableLogs: true,
    _experiments: {
      enableMetrics: true,
    },
    tracesSampleRate,
    tracePropagationTargets: API_TRACE_TARGETS,
    integrations: buildIntegrations(router, isWeb),
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    profileSessionSampleRate,
    profileLifecycle: 'trace',
    enableNative: Capacitor.isNativePlatform(),
    siblingOptions: {
      vueOptions: {
        app,
        attachProps: true,
        attachErrorHandler: true,
      },
    },
  } as CapacitorOptions & {
    replaysSessionSampleRate: number;
    replaysOnErrorSampleRate: number;
    profileSessionSampleRate: number;
    profileLifecycle: 'trace';
  };

  Sentry.init(options, SentryVue.init);
}

export { Sentry };
export { nativeCrash, close } from '@sentry/capacitor';
